// Tabla «opciones nuevas» de un módulo, plegada por defecto.
//
// Hermana de RenombresMenu, pero para el caso contrario: aquella responde «la
// opción que usaba ya no se llama así»; esta responde «apareció algo que no
// existía y no sé dónde está». No caben en la misma tabla: RenombresMenu tacha
// el nombre viejo, y una opción nueva no tiene nombre viejo que tachar.
//
// Se añadió con las seis olas del panel de auditores (scripts 775-786), que
// sumaron pantallas a Contabilidad, Control Bancario y Financiero. Cuando estas
// opciones dejen de ser nuevas para el usuario, retirar el bloque del módulo.
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export interface OpcionNueva {
  /** Nombre de la opción del menú, o de la función nueva dentro de una pantalla. */
  opcion: string;
  /** Dónde vive: «Menú › Submenú», o el nombre de la pantalla que la contiene. */
  donde: string;
  /** Para qué sirve, en una frase. */
  que: string;
  /** Nivel de acceso mínimo, si está restringida. */
  nivel?: string;
}

interface Props {
  filas: OpcionNueva[];
  nota: string;
  /** Título del bloque. Por defecto habla de opciones nuevas; un módulo donde lo
   *  nuevo son funciones DENTRO de pantallas que ya existían debe cambiarlo:
   *  decir «opciones nuevas» y no añadir ninguna al menú desorienta más de lo
   *  que ayuda. */
  titulo?: string;
  /** Texto del botón que despliega la tabla. */
  cta?: string;
  /** Encabezado de la primera columna. */
  colOpcion?: string;
}

export function OpcionesNuevas({ filas, nota, titulo, cta, colOpcion }: Props) {
  const [abierto, setAbierto] = useState(false);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-5 w-5 text-primary" /> {titulo ?? 'Hay opciones nuevas en este módulo'}
          <button className="ml-auto text-sm font-normal text-primary hover:underline"
            onClick={() => setAbierto((v) => !v)}>
            {abierto ? 'Ocultar' : (cta ?? `Ver las ${filas.length} opciones nuevas`)}
          </button>
        </CardTitle>
      </CardHeader>
      {abierto && (
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">{nota}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left">{colOpcion ?? 'Opción'}</th>
                  <th className="py-2 pr-3 text-left">Dónde está</th>
                  <th className="py-2 text-left">Para qué sirve</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((r, i) => (
                  <tr key={i} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">
                      {r.opcion}
                      {r.nivel && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">{r.nivel}</span>}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{r.donde}</td>
                    <td className="py-2 text-muted-foreground">{r.que}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
